
import { supabase } from "./supabase/client";
import { ClassType } from "./supabase/types";
import { fetchClasses } from "./supabase/database";
import { format } from "date-fns";

/**
 * Days of the week for scheduling
 */
const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/**
 * Template for class times - adjust as needed
 * Example: For each day that has classes, specify the times and other details
 */
const CLASS_TEMPLATES: Record<string, Omit<Omit<ClassType, 'id'>, 'date'>[]> = {
  "Tuesday": [
    {
      title: "Pottery Class",
      day: "Tuesday",
      time: "7:00 PM",
      duration: "1.5",
      capacity: 12,
      availableSpots: 12,
      image: "https://images.unsplash.com/photo-1620140036708-455ed5c0426a",
      price: 3500 // Ensure price is set
    }
  ],
  "Wednesday": [
    {
      title: "Pottery Class",
      day: "Wednesday",
      time: "7:00 PM",
      duration: "1.5",
      capacity: 12,
      availableSpots: 12,
      image: "https://images.unsplash.com/photo-1610701596007-11502861dcfa",
      price: 3500 // Ensure price is set
    }
  ],
  "Thursday": [
    {
      title: "Pottery Class",
      day: "Thursday",
      time: "7:00 PM",
      duration: "1.5",
      capacity: 12,
      availableSpots: 12,
      image: "https://images.unsplash.com/photo-1595351298020-038700609878",
      price: 3500 // Ensure price is set
    }
  ],
  "Saturday": [
    {
      title: "Pottery Class",
      day: "Saturday",
      time: "2:00 PM",
      duration: "1.5",
      capacity: 12,
      availableSpots: 12,
      image: "",
      price: 3500 // Ensure price is set
    },
    {
      title: "Pottery Class",
      day: "Saturday",
      time: "7:00 PM",
      duration: "1.5",
      capacity: 12,
      availableSpots: 12,
      image: "",
      price: 3500 // Ensure price is set
    }
  ]
};

/**
 * Get the date for a specific day of the week in a given week
 */
const getDateForDayInWeek = (dayName: string, weekOffset: number): Date => {
  const today = new Date();
  const dayOfWeek = DAYS_OF_WEEK.indexOf(dayName);
  
  if (dayOfWeek === -1) {
    throw new Error(`Invalid day name: ${dayName}`);
  }
  
  const resultDate = new Date(today);
  const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  // Convert to same scale (0 = Monday in our array, but 0 = Sunday in JS Date)
  const adjustedCurrentDay = currentDay === 0 ? 6 : currentDay - 1;
  const adjustedTargetDay = dayOfWeek;
  
  // Calculate days to add
  let daysToAdd = adjustedTargetDay - adjustedCurrentDay;
  
  // If the target day is earlier in the week, add days to get to next week
  if (daysToAdd <= 0) {
    daysToAdd += 7;
  }
  
  // Add offset for future weeks
  daysToAdd += weekOffset * 7;
  
  resultDate.setDate(today.getDate() + daysToAdd);
  return resultDate;
};

/**
 * Create classes for the upcoming weeks
 */
export const scheduleClassesForUpcomingWeeks = async (
  weeksAhead: number = 8
): Promise<{ success: boolean; created: number; error?: string }> => {
  try {
    let createdCount = 0;
    
    // Get existing classes to avoid duplicates
    const existingClasses = await fetchClasses();
    const existingClassMap: Record<string, boolean> = {};
    
    // Create a map for easy lookup of existing classes by date and time
    existingClasses.forEach(cls => {
      const key = `${cls.date}-${cls.time}`;
      existingClassMap[key] = true;
    });
    
    console.log(`Scheduling classes for ${weeksAhead} weeks ahead with date-based tracking`);
    console.log('Existing classes map:', existingClassMap);
    
    // For each week ahead
    for (let week = 0; week < weeksAhead; week++) {
      // For each day that has class templates
      for (const day of Object.keys(CLASS_TEMPLATES)) {
        const date = getDateForDayInWeek(day, week);
        const dateStr = format(date, 'yyyy-MM-dd'); // YYYY-MM-DD
        const formattedDate = format(date, 'MMM d, yyyy'); // e.g., "Apr 15, 2025"
        
        // For each class template on this day
        for (const template of CLASS_TEMPLATES[day]) {
          const key = `${dateStr}-${template.time}`;
          
          // Skip if this class already exists
          if (existingClassMap[key]) {
            console.log(`Skipping existing class: ${key}`);
            continue;
          }
          
          // Create the class
          try {
            console.log(`Creating class for: ${day} (${formattedDate}) at ${template.time}, week ${week + 1}`);
            
            // Prepare the data - ensure field names match the database columns
            const classData = {
              title: template.title,
              day: template.day,
              date: dateStr,
              time: template.time,
              duration: template.duration,
              capacity: template.capacity,
              availablespots: template.availableSpots, // lowercase to match DB column
              image: template.image,
              price: template.price // Add price to the class data
            };
            
            const { error } = await supabase
              .from('classes')
              .insert(classData);
            
            if (error) {
              console.error('Error creating scheduled class:', error);
              continue;
            }
            
            createdCount++;
            // Mark as existing to avoid duplicates
            existingClassMap[key] = true;
          } catch (error) {
            console.error('Error creating scheduled class:', error);
          }
        }
      }
    }
    
    return { success: true, created: createdCount };
  } catch (error: any) {
    console.error('Error scheduling classes:', error);
    return { success: false, created: 0, error: error.message };
  }
};

/**
 * Initialize classes - can be called on app startup
 */
export const initializeClasses = async (): Promise<void> => {
  try {
    const { data, error } = await supabase
      .from('classes')
      .select('id')
      .limit(1);
    
    // If there are no classes or error fetching, create initial classes
    if (error || (data && data.length === 0)) {
      await scheduleClassesForUpcomingWeeks(8);
    }
  } catch (error) {
    console.error('Error initializing classes:', error);
  }
};
